import { type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { IconLoader2 } from "@tabler/icons-react";
import Loader from "../../../components/Loader";
import { PRACTICE_FORM_ID } from "../useOnboardingFlow";

/**
 * The agent's two screens: the wait, then what it found (practice-profiles
 * tickets 09 and 11).
 *
 * Both are STATES of the "What do you practise?" slide, not slides of their own:
 * the title, the progress bar and the footer band stay exactly where they are,
 * and only the panel under them changes. Anything else reads as a slide the flow
 * gained and then lost — the carousel's own transition is already running when
 * this mounts, and a header or footer that changes shape mid-transition moves
 * every other slide with it (the footer band's height is what pads them all).
 * The recap's second action lives HERE, in the body, for that reason.
 *
 * `reading` names what is actually being looked for — where they practise, and
 * what that says about their practice. The three lines appear on a TIMER, not on
 * server progress: claiming to reflect real steps would mean opening a progress
 * channel for information nobody acts on, and the honest version reads the same.
 * What is not done is the other register: no "our AI is analysing your profile".
 *
 * `recap` is what the list used to do badly. The agent's answer arrived as ticks
 * on nineteen rows, which is a screen asking to be arbitrated rather than read —
 * and the way past it was "Check all", so a measurable share of accounts claimed
 * their whole catalogue. Here the same answer is one sentence to read, Continue
 * accepts it, and "Edit" opens the list for anyone who wants it.
 *
 * Purely presentational: no state, no effects, no refs. The staging is
 * declarative `motion` delays, the names arrive translated, and the state machine
 * that decides which of the two renders lives in `useOnboardingFlow`.
 */

/**
 * How many practices the recap sentence NAMES before it counts the rest.
 *
 * Stacked cards read as a list to scroll, so the answer is one sentence, and
 * past three names a sentence is a list again. One practice left over is named
 * rather than counted: "1 other" hides a name for no gain in length.
 */
const PRACTICE_RECAP_MAX_NAMED = 3;

export function PracticeAgentPanel(
  props:
    | {
        status: "reading";
        firstName: string;
        /**
         * The declared Specialty, which the desktop reading names (Figma
         * "D7.pratiques.lecture"). Null before its label resolves.
         */
        specialtyLabel: string | null;
        /** `desktop` is the desktop onboarding's drawing of the wait. */
        variant: "mobile" | "desktop";
      }
    | {
        status: "recap";
        names: readonly string[];
        onEdit: () => void;
        onSubmit: () => void;
      }
): React.JSX.Element {
  const { t, i18n } = useTranslation();

  if (props.status === "recap") {
    const named =
      props.names.length <= PRACTICE_RECAP_MAX_NAMED + 1
        ? props.names
        : props.names.slice(0, PRACTICE_RECAP_MAX_NAMED);
    const unnamed = props.names.length - named.length;
    const items =
      unnamed === 0
        ? named
        : [...named, t("{{count}} others", { count: unnamed })];
    // The language's own separators and "and", as parts so each name can be
    // highlighted where the list puts it.
    const parts = new Intl.ListFormat(i18n.language, {
      type: "conjunction",
    }).formatToParts(items);
    const practices = parts.map((part) => part.value).join("");
    // One sentence, one key: the list sits where the translation puts it.
    const sentence = t("You practise in these areas: {{practices}}.", {
      count: props.names.length,
      practices,
    });
    const at = sentence.indexOf(practices);
    if (at === -1)
      throw new Error("should not happen: the recap lost its practices");
    // The count of the rest is an element too, and stays plain.
    let element = -1;
    const segments = parts.map((part) => {
      if (part.type === "element") element += 1;
      return {
        highlighted: part.type === "element" && element < named.length,
        text: part.value,
      };
    });
    return (
      // A FORM, like the catalogue's own panel, and under the same id: the
      // footer's Continue is a submit button outside this tree and reaches its
      // question through `form=`. Rendered as a plain div, the recap left that
      // id pointing at nothing, so the only enabled button on the screen did
      // nothing at all.
      //
      // CENTRED, unlike every other panel of this slide. A recap is two or three
      // lines against a slide sized for nineteen rows, and top-aligned it reads
      // as a screen that failed to load the rest. The picker stays top-aligned
      // and so does the wait: this is the one state whose content is the whole
      // answer, so the empty space below it is not room for anything.
      <form
        id={PRACTICE_FORM_ID}
        className="flex w-full min-h-0 flex-1 flex-col justify-center gap-5"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          props.onSubmit();
        }}
      >
        <p className="m-0 text-lg/relaxed text-ds-slate-900">
          {sentence.slice(0, at)}
          {segments.map((segment, index) =>
            segment.highlighted ? (
              // Moss-900 on moss-100 reads at 10.5:1. `box-decoration-clone`
              // keeps the highlight whole on each line of a wrapped name.
              <span
                key={index}
                className="box-decoration-clone rounded-md bg-ds-moss-100 px-1 font-medium text-ds-moss-900"
              >
                {segment.text}
              </span>
            ) : (
              segment.text
            )
          )}
          {sentence.slice(at + practices.length)}
        </p>
        {/* `type="button"`: inside a form, the default is submit, and this one
            opens the catalogue rather than leaving the slide. */}
        <button
          type="button"
          className="cursor-pointer self-start border-none bg-transparent p-0 font-dm-sans text-sm font-medium text-ds-mint-800 underline"
          onClick={props.onEdit}
        >
          {t("Edit my practices")}
        </button>
        {/* The reason Continue is allowed to accept a machine's answer: nothing
            about it is final. Printed here rather than over the CTA, where the
            axis question already prints its own note — two reassurances stacked
            in one band is neither of them read. */}
        <p className="m-0 text-ui-note text-ds-slate-600">
          {t("You can change them at any time in your settings.")}
        </p>
      </form>
    );
  }

  // Arrival delay in seconds, spread across the server's budget so the last line
  // is still on screen when the answer lands. Paired with its line rather than
  // held in a parallel array: motion's `delay` accepts `undefined`, so a fourth
  // line against three delays would animate in instantly and typecheck clean.
  // In the component, not at module scope, because the i18n scanner only extracts
  // literal `t("…")`.
  if (props.variant === "desktop") {
    // The desktop drawing shows every line from the start, the ones still to
    // come at a third of their weight: the reader sees the whole wait at once.
    const desktopNarration = [
      { delay: 0, line: t("Finding your workplace") },
      { delay: 4, line: t("Reading your publications") },
      {
        delay: 8,
        line:
          props.specialtyLabel === null
            ? t("Matching them to the practices in your specialty")
            : t("Matching them to {{specialty}} practices", {
                specialty: props.specialtyLabel,
              }),
      },
    ];
    return (
      <div className="flex w-full min-h-0 flex-1 flex-col gap-4">
        <div className="flex shrink-0 items-center gap-2">
          <IconLoader2
            aria-hidden
            className="size-4 animate-spin text-ds-mint-800"
          />
          <p className="m-0 text-sm font-medium text-ds-mint-800">
            {t("Finding your practices")}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          {desktopNarration.map((step) => (
            <motion.p
              key={step.line}
              className="m-0 text-sm text-ds-slate-600"
              initial={{ opacity: 0.35 }}
              animate={{ opacity: 1 }}
              transition={{ delay: step.delay, duration: 0.4 }}
            >
              {step.line}
            </motion.p>
          ))}
        </div>
      </div>
    );
  }

  const narration = [
    { delay: 0, line: t("Looking for where you practise") },
    { delay: 4, line: t("Reading what your work says about your practice") },
    { delay: 8, line: t("Matching them to the practices in your specialty") },
  ];

  return (
    <div className="flex w-full min-h-0 flex-1 flex-col gap-4">
      <div className="flex shrink-0 items-center gap-2">
        <Loader />
        {props.firstName.length > 0 ? (
          <p className="m-0 text-sm font-medium tracking-tight text-ds-mint-800">
            {t("One moment, {{firstName}}", { firstName: props.firstName })}
          </p>
        ) : null}
      </div>
      <div className="flex flex-col gap-2">
        {narration.map((step) => (
          <motion.p
            key={step.line}
            className="m-0 text-sm text-ds-slate-600"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: step.delay, duration: 0.4 }}
          >
            {step.line}
          </motion.p>
        ))}
      </div>
    </div>
  );
}
